import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ChevronRight, Loader2 } from "lucide-react";
import { setCanonical, setMetaTags, SpeakableSchema } from "@/components/seo/SchemaMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "", label: "All" },
  { value: "storage", label: "Storage" },
  { value: "parking", label: "Parking" },
  { value: "moving", label: "Moving" },
  { value: "decluttering", label: "Decluttering" },
];

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  useEffect(() => {
    setCanonical("/blog");
    setMetaTags({
      title: "Storage, Parking & Moving Blog | Storajini Guide Philippines",
      description: "Read expert tips and guides about storage solutions, parking, moving, and decluttering in the Philippines.",
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = category ? `?category=${category}` : "";
    axios.get(`${API}/blog${params}`)
      .then(r => setPosts(r.data.posts))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div data-testid="blog-list-page">
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">Blog & Guides</h1>
          <p className="text-gray-300 text-lg">Expert tips on storage, parking, moving, and organization in the Philippines.</p>
        </div>
      </section>

      {/* Category filter */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex gap-2 overflow-x-auto">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategory(cat.value)}
                data-testid={`blog-filter-${cat.value || "all"}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  category === cat.value
                    ? "bg-amber-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts */}
      <div className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No posts found in this category yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group bg-white rounded-xl border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2.5 py-0.5 bg-amber-50 text-amber-600 text-xs font-semibold rounded-full capitalize">{post.category}</span>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.created_at).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 group-hover:text-amber-600 transition-colors mb-2 line-clamp-2">{post.title}</h2>
                    <p className="text-gray-500 text-sm line-clamp-3 leading-relaxed">{post.excerpt}</p>
                    <span className="inline-flex items-center mt-4 text-amber-600 font-semibold text-sm">
                      Read More <ChevronRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
